import { InlineEdit } from '@lmc/ui-kit'
import { Heading } from '@lmc/ui-kit/src/general'
import { Form } from 'antd'
import type { Dispatch, FC, SetStateAction } from 'react'
import styled from 'styled-components'
import { commonPatterns } from '../../../../utils/enhancedTestIds'

type InlineNameEditProps = {
  value: string
  label: string
  isInlineEditing?: boolean
  handleUpdateDevice?: (name: string) => void
  setIsInlinEditing?: Dispatch<SetStateAction<boolean | undefined>>
  disabled?: boolean
  error?: string | undefined
  isLoading?: boolean
  onReset?: () => void
  /**
   * 'field' (default) renders a labelled form row, used for read-only
   * details like Model name / App version. 'title' renders the value as a
   * bold heading with an inline edit affordance, used for the device name.
   */
  variant?: 'field' | 'title'
  /** Label for the secondary edit button (title variant). */
  editLabel?: string
  testId?: string
}

const mapError = (error?: string) => {
  if (error === 'Field [name] must not exceed 20 characters') {
    return 'Name must not exceed 20 characters'
  }
  return error
}

const InlineNameEdit: FC<InlineNameEditProps> = ({
  value,
  label,
  handleUpdateDevice,
  setIsInlinEditing,
  disabled,
  error,
  isLoading,
  onReset,
  variant = 'field',
  editLabel,
  testId
}) => {
  const inlineEdit = (
    <InlineEdit
      disabled={disabled}
      value={value}
      editLabel={variant === 'title' ? editLabel : undefined}
      onChange={(name) => handleUpdateDevice?.(name)}
      isEditing={isLoading || !!error}
      onEditStateChange={(editState) => {
        setIsInlinEditing?.(editState)
      }}
      onCancel={() => {
        if (error) {
          onReset?.()
        }
      }}
      textComponent={
        variant === 'title'
          ? (props) => <TitleHeading level={4} {...props} />
          : undefined
      }
      data-testid={
        testId ? commonPatterns.input(testId, 'inline-edit') : undefined
      }
    />
  )

  if (variant === 'title') {
    return <TitleWrapper data-testid={testId}>{inlineEdit}</TitleWrapper>
  }

  return (
    <FormItemStyled
      label={label}
      validateStatus={error ? 'error' : undefined}
      help={mapError(error) || undefined}
      style={{ minWidth: 200 }}
      data-testid={testId}
    >
      {inlineEdit}
    </FormItemStyled>
  )
}

const TitleWrapper = styled.div``

const TitleHeading = styled(Heading)`
  margin: 0;
` as typeof Heading

const FormItemStyled = styled(Form.Item)`
  .ant-form-item-label {
    min-width: 120px;
    text-align: start !important;
  }

  .ant-form-item-label > label {
    color: ${({ theme }) => theme.color.neutral.color4};
  }

  margin-top: 5px;
  margin-bottom: 5px;

  .ant-form-item-control-input-content > div > div {
    font-weight: 500;
  }
`

export default InlineNameEdit
